import React, { useCallback, useEffect, useState } from 'react';
import { getFacilitiesDetails, getAllCustomNoteType, getPatientIdList } from '../../api/list';
import Select from "react-select";
import Tabs from '../../components/tabs';
import Editor from './Editmain';
import { debounce } from "../../utils/redirections";

interface Facility {
    facilityName: string;
    id: number;
}

interface NoteTypes {
    note_type: string;
    id: number;
}

interface Patient {
    id: number;
    firstName: string;
    lastName: string;
}

const NoteType = () => {
    const [activeTab, setActiveTab] = useState("New");
    const [formExpanded, setFormExpanded] = useState<boolean>(true);
    const [noteTypes, setNoteTypes] = useState<NoteTypes[]>([]);
    const [selectedNoteType, setSelectedNoteType] = useState<NoteTypes | null>(null);
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
    const [patients, setPatients] = useState<Patient[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<{ value: number; 
        label: string; mrn: number } | null>(null);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchFacilities = async () => {
            try {
                const result = await getFacilitiesDetails();
                if (result?.result) setFacilities(result.result.data);
            } catch (error) {
                console.error("Error fetching facilities:", error);
            }
        };

        const fetchNoteTypes = async () => {
            try {
                const result = await getAllCustomNoteType();
                if (result?.result) setNoteTypes(result.result.data);
            } catch (error) {
                console.error("Error fetching note types:", error);
            }
        };

        fetchFacilities();
        fetchNoteTypes();
    }, []);

    const fetchPatients = async (name: string) => {
        try {
            const result = await getPatientIdList({ name });
            setPatients(result?.result?.data || []);
        } catch (error) {
            console.error("Error fetching patient data:", error);
        }
    };

    //eslint-disable-next-line
    const debouncedFetchPatients = useCallback(debounce(fetchPatients, 500), []);

    useEffect(() => {
        if (searchTerm && selectedFacility) {
            debouncedFetchPatients(searchTerm);
        }
    }, [searchTerm, selectedFacility, debouncedFetchPatients]);

    const options = patients.map((patient) => ({
        value: patient.id,
        label: `${patient.lastName} ${patient.firstName} (MRN: ${patient.id})`,
        mrn: patient.id,
    }));





    return (
        <div className="p-2 bg-white rounded-lg shadow-md">
            <div className='flex justify-center'>
                <div className='w-[30%] mx-auto'>
                    <Tabs
                        tabs={["new", "my notes", "draft"]}
                        value={activeTab}
                        onChange={setActiveTab}
                        className="mb-1 border border-gray-200 rounded-lg"
                    />
                </div>
            </div>
            <div className="w-full flex rounded-2xl border">
                <div className={`${formExpanded ? "w-[30%] p-2" : "w-12"} transition-all duration-500`}>
                    <button onClick={() => setFormExpanded(!formExpanded)} className="bg-[#3b98b3] hover:bg-[#215564] text-white px-3 py-1 rounded-md">
                        {formExpanded ? "<< " : ">>"}
                    </button>

                    {formExpanded && (
                        <div className="transition-all ease-in-out">
                            <h2 className="text-2xl font-medium text-center text-gray-800 mb-2">Create a Note</h2>

                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Select Note Type:</label>
                                <select
                                    className="w-full px-4 py-1 border border-gray-300 rounded-lg"
                                    value={selectedNoteType?.id || ""}
                                    onChange={(e) => {
                                        const note = noteTypes
                                        .find(n => n.id === Number(e.target.value));
                                        setSelectedNoteType(note || null);
                                    }}
                                >
                                    <option value="">Select Note Type</option>
                                    {noteTypes.map(note => (
                                        <option key={note.id} value={note.id}>
                                            {note.note_type}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-medium mb-1">Select Facility:</label>
                                <select
                                    className="w-full px-4 py-1 border border-gray-300 rounded-lg"
                                    value={selectedFacility?.id || ""}
                                    onChange={(e) => {
                                        const facility = facilities
                                            .find(f => f.id === Number(e.target.value));
                                        setSelectedFacility(facility || null);
                                    }}
                                >
                                    <option value="">Select Facility</option>
                                    {facilities.map(facility => (
                                        <option key={facility.id} value={facility.id}>
                                            {facility.facilityName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <Select
                                options={options}
                                onChange={(selectedOption) => {
                                    setSelectedPatient(selectedOption);

                                }}
                                onInputChange={(inputValue) => {
                                    setSearchTerm(inputValue);
                                }}
                                value={selectedPatient}
                                placeholder="Select a patient"
                            />

                            {selectedPatient && (
                                <div className="mt-4 p-2 border rounded-lg">
                                    <p>
                                        <strong>Selected Patient:</strong>
                                        {selectedPatient.label}
                                    </p>
                                    <p><strong>MRN:</strong> {selectedPatient.mrn}</p>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button className="px-6 py-2 bg-[#3b98b3] 
                                hover:bg-[#215564] text-white rounded-lg">Submit</button>
                            </div>
                        </div>
                    )}
                </div>
                <Editor />
            </div>
        </div>
    );
};

export default NoteType;
